import { useCallback, useState } from 'react';

import type { KnowledgeBase } from '@mint-sdk/api-ai-assistant';

export type FolderAccess = 'invited' | 'workspace';

export type FolderAccessState = {
  access: FolderAccess;
  ids: string[];
};

type Options = {
  initial?: FolderAccessState;
  meId?: string;
};

export type FolderAccessDraft = FolderAccessState & {
  setAccess: (access: FolderAccess) => void;
  add: (ids: string[]) => void;
  remove: (id: string) => void;
  isDirty: boolean;
  hasRecipients: boolean;
  toRequest: () => KnowledgeBase.SetFolderGrantsRequest;
};

const EMPTY: FolderAccessState = { access: 'invited', ids: [] };

const isSameSet = (left: string[], right: string[]) => {
  const rightSet = new Set(right);

  return (
    new Set(left).size === rightSet.size && left.every((id) => rightSet.has(id))
  );
};

export const useFolderAccessDraft = ({
  initial = EMPTY,
  meId,
}: Options): FolderAccessDraft => {
  const [draft, setDraft] = useState(() => ({
    saved: initial,
    access: initial.access,
    ids: initial.ids,
  }));
  const { saved, access, ids } = draft;

  const setAccess = useCallback(
    (nextAccess: FolderAccess) =>
      setDraft((current) => ({ ...current, access: nextAccess })),
    [],
  );

  const add = useCallback((added: string[]) => {
    return setDraft((current) => ({
      ...current,
      ids: [...new Set([...current.ids, ...added])],
    }));
  }, []);

  const remove = useCallback(
    (removed: string) =>
      setDraft((current) => ({
        ...current,
        ids: current.ids.filter((id) => id !== removed),
      })),
    [],
  );

  const isDirty = access !== saved.access || !isSameSet(ids, saved.ids);

  const hasRecipients = access === 'workspace' || ids.some((id) => id !== meId);

  const toRequest = (): KnowledgeBase.SetFolderGrantsRequest => {
    const allWorkspace = access === 'workspace';

    return {
      allWorkspace,
      grants: allWorkspace
        ? []
        : ids.map((granteeId) => ({ granteeType: 'user', granteeId })),
    };
  };

  return {
    access,
    ids,
    setAccess,
    add,
    remove,
    isDirty,
    hasRecipients,
    toRequest,
  };
};
